'use client';

import { getPusherClient } from '@/lib/pusher-client';
import { clearLocalAuthCookies } from '@/server/actions/auth-actions';
import { useAuthStore } from '@/store/auth-store';
import { useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Global listener for force-logout events.
 * Mounts at root layout so it works on ALL pages, not just protected routes.
 * Only subscribes when the user is authenticated.
 */
export function ForceLogoutListener() {
  const user = useAuthStore((state) => state.user);
  const signOut = useAuthStore((state) => state.signOut);
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;

    const pusherClient = getPusherClient();
    if (!pusherClient) return;

    // Listen to a user-specific channel for force-logout events
    const channelName = `user-${userId}`;
    const channel = pusherClient.subscribe(channelName);

    channel.bind('force-logout', async (data: { reason?: string }) => {
      console.log('[ForceLogout] Received force logout event:', data);

      if (data?.reason) {
        toast.error(data.reason);
      }

      try {
        await signOut();
      } catch (error) {
        console.error('[ForceLogout] Failed to sign out:', error);
      } finally {
        await clearLocalAuthCookies();
        window.location.href = '/?auth=login&reason=session_ended';
      }
    });

    return () => {
      channel.unbind('force-logout');
      pusherClient.unsubscribe(channelName);
    };
  }, [userId, signOut]);

  return null;
}
